import { useEffect, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";

import {
  FiCalendar,
  FiDollarSign,
  FiUsers,
  FiTrendingUp,
  FiBarChart2,
  FiSend,
  FiActivity,
  FiEye,
  FiPercent,
  FiRefreshCw,
} from "react-icons/fi";

import "./AdminDashboard.css";


const AdminDashboard = () => {
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState(getFormattedDate(30));
  const [endDate, setEndDate] = useState(getFormattedDate(0));
  const [selectedRange, setSelectedRange] = useState("last30");

  const [metrics, setMetrics] = useState(null);
  const [tabsData, setTabsData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [activeDataTab, setActiveDataTab] = useState("day_wise");

  // ---------------- DATE FUNCTIONS ----------------

  function getFormattedDate(daysAgo = 0) {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);

    return date.toISOString().split("T")[0];
  }

  function getFirstDayOfMonth() {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1)
      .toISOString()
      .split("T")[0];
  }

  function getFirstDayOfLastMonth() {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth() - 1, 1)
      .toISOString()
      .split("T")[0];
  }

  function getLastDayOfLastMonth() {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 0)
      .toISOString()
      .split("T")[0];
  }


  // ---------------- QUICK DATE FILTER ----------------

  const handleRangeChange = (e) => {
    const range = e.target.value;
    setSelectedRange(range);

    switch (range) {
      // case "today":
      //   setStartDate(getFormattedDate(0));
      //   setEndDate(getFormattedDate(0));
      //   break;

      case "yesterday":
        setStartDate(getFormattedDate(1));
        setEndDate(getFormattedDate(1));
        break;

      case "last7":
        setStartDate(getFormattedDate(7));
        setEndDate(getFormattedDate(0));
        break;

      case "last15":
        setStartDate(getFormattedDate(15));
        setEndDate(getFormattedDate(0));
        break;

      case "last30":
        setStartDate(getFormattedDate(30));
        setEndDate(getFormattedDate(0));
        break;

      case "thisMonth":
        setStartDate(getFirstDayOfMonth());
        setEndDate(getFormattedDate(0));
        break;

      case "lastMonth":
        setStartDate(getFirstDayOfLastMonth());
        setEndDate(getLastDayOfLastMonth());
        break;

      case "custom":
        break;

      default:
        break;
    }
  };


  // ---------------- FETCH DASHBOARD DATA ----------------

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const token = localStorage.getItem("token");

      if (!token) {
        navigate("/");
        return;
      }

      const payload = {
        startDate,
        endDate,
      };

      const headers = {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      };

      const { role } = jwtDecode(token);
      console.log(role);
      
      const endPoint = (role === "ad_ops" || role === "account_manager") ? "dashboardUserMetrics" : "dashboardMetrics";

      // Metrics API
      const metricsResponse = await axios.post(
        `${API_BASE_URL}/api/admin/${endPoint}`,
        payload,
        { headers }
      );


      // Tabs API
      const tabEndPoint = (role === "ad_ops" || role === "account_manager") ? "dashboardUserDataTabs" : "dashboardDataTabs";
      
      const tabsResponse = await axios.post(
        `${API_BASE_URL}/api/admin/${tabEndPoint}`,
        payload,
        { headers }
      );


      setMetrics(metricsResponse?.data?.data || {});
      setTabsData(tabsResponse?.data?.data || tabsResponse?.data || {});

    } catch (error) {

      console.error("Dashboard Error:", error);

      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/");
      } else {
        setErrorMessage(
          error.response?.data?.message || "Unable to fetch dashboard data."
        );
      }

    } finally {
      setLoading(false);
    }
  };


  // Initial API Call
  useEffect(() => {
    fetchDashboardData();
  }, []);


  // ---------------- FORMAT NUMBERS ----------------

  const formatNumber = (value) => {
    if (value === null || value === undefined) return "0";

    return new Intl.NumberFormat("en-IN").format(value);
  };


  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "$0.00";

    return `$${Number(value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };


  // ---------------- HANDLE SUBMIT ----------------

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchDashboardData();
  };


  // ---------------- METRIC CARDS ----------------

const cards = [
  {
    title: "Revenue",
    value: `$${formatNumber(metrics?.total_revenue)}`,
    subtitle: "Total Revenue",
    icon: <FiDollarSign />,
    className: "revenue-card",
  },
  {
    title: "Publisher Revenue",
    value: `$${formatNumber(metrics?.total_pub_revenue)}`,
    subtitle: "Publisher Earnings",
    icon: <FiUsers />,
    className: "publisher-card",
  },
  {
    title: "Margin",
    value: `${metrics?.margin ?? 0}%`,
    subtitle: "Profit Margin",
    icon: <FiTrendingUp />,
    className: "margin-card",
  },
  {
    title: "Impressions",
    value: formatNumber(metrics?.impressions),
    subtitle: "Total Impressions",
    icon: <FiBarChart2 />,
    className: "impression-card",
  },

  // Additional metrics — same card format
  {
    title: "Original Requests",
    value: formatNumber(metrics?.orig_requests),
    subtitle: "Original Requests",
    icon: <FiSend />,
    className: "request-card",
  },
  {
    title: "Requests",
    value: formatNumber(metrics?.requests),
    subtitle: "Total Requests",
    icon: <FiActivity />,
    className: "request-card",
  },
  {
    title: "Original Impressions",
    value: formatNumber(metrics?.orig_impressions),
    subtitle: "Original Impressions",
    icon: <FiEye />,
    className: "impression-card",
  },
  {
    title: "Fill Rate",
    value: `${metrics?.fill_rate ?? 0}%`,
    subtitle: "Average Fill Rate",
    icon: <FiPercent />,
    className: "fillrate-card",
  },
  {
    title: "Publisher eCPM",
    value: `$${Number(metrics?.pub_ecpm ?? 0).toFixed(2)}`,
    subtitle: "Publisher eCPM",
    icon: <FiDollarSign />,
    className: "ecpm-card",
  },
  {
    title: "eCPM",
    value: `$${Number(metrics?.ecpm ?? 0).toFixed(2)}`,
    subtitle: "Average eCPM",
    icon: <FiTrendingUp />,
    className: "ecpm-card",
  },
];
//Table data
const formatColumnName = (column) => {
  return column
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatTableValue = (column, value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  // Date formatting
  if (column === "date") {
    return value;
  }

  // Currency fields
  if (
    ["pub_revenue", "revenue", "pub_ecpm", "ecpm"].includes(column)
  ) {
    return Number(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  // Percentage fields
  if (
    ["fill_rate", "ctr", "margin"].includes(column)
  ) {
    return `${Number(value).toFixed(2)}%`;
  }

  // Number fields
  if (typeof value === "number") {
    return formatNumber(value);
  }

  return value;
};
  return (
    <section className="dashboard-container">

      {/* PAGE HEADER */}
      {/* <div className="dashboard-header">
        <div>
          <h1>Dashboard</h1>
          <p>Overview of your advertising performance</p>
        </div>

        <button
          className="refresh-btn"
          onClick={fetchDashboardData}
          disabled={loading}
        >
          <FiRefreshCw className={loading ? "spin" : ""} />
          Refresh
        </button>
      </div> */}


      {/* DATE FILTER */}
      <div className="dashboard-filter-card">

        <div className="filter-title">
          <FiCalendar />
          <span>Dashboard Overview</span>
        </div>

        <form onSubmit={handleSubmit} className="dashboard-filter-form">

          <div className="filter-group">
            <label>Quick Date Range</label>

            <select
              value={selectedRange}
              onChange={handleRangeChange}
            >
              {/* <option value="today">Today</option> */}
              <option value="yesterday">Yesterday</option>
              <option value="last7">Last 7 Days</option>
              <option value="last15">Last 15 Days</option>
              <option value="last30">Last 30 Days</option>
              <option value="thisMonth">This Month</option>
              <option value="lastMonth">Last Month</option>
              <option value="custom">Custom</option>
            </select>
          </div>


          {selectedRange === "custom" && (
            <>
              <div className="filter-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </>
          )}


          <button
            type="submit"
            className="submit-filter-btn"
            disabled={loading}
          >
            {loading ? "Loading..." : "Apply Filter"}
          </button>

        </form>

        {/* <div className="selected-date-info">
          Showing data from <strong>{startDate}</strong> to{" "}
          <strong>{endDate}</strong>
        </div> */}

      </div>


      {/* ERROR */}
      {errorMessage && (
        <div className="dashboard-error">
          {errorMessage}
        </div>
      )}


      {/* SUMMARY CARDS */}
      {/* SUMMARY CARDS */}
<div className="dashboard-cards-grid">

  {cards.map((card, index) => (
    <div
      className={`dashboard-card ${card.className}`}
      key={index}
    >

      <div className="card-content">

        <h4>{card.title}</h4>

        <h2>{card.value}</h2>

        <div className="card-subtitle">
          <FiCalendar />
          <span>{card.subtitle}</span>
        </div>

      </div>

      <div className="card-icon">
        {card.icon}
      </div>

      <div className="card-background-icon">
        {card.icon}
      </div>

    </div>
  ))}

</div>


      {/* ADDITIONAL METRICS */}
      {/* <div className="additional-metrics">

        <div className="metric-box">
          <span>Original Requests</span>
          <strong>{formatNumber(metrics?.orig_requests)}</strong>
        </div>

        <div className="metric-box">
          <span>Requests</span>
          <strong>{formatNumber(metrics?.requests)}</strong>
        </div>

        <div className="metric-box">
          <span>Original Impressions</span>
          <strong>{formatNumber(metrics?.orig_impressions)}</strong>
        </div>

        <div className="metric-box">
          <span>Fill Rate</span>
          <strong>{metrics?.fill_rate ?? 0}%</strong>
        </div>

        <div className="metric-box">
          <span>Publisher eCPM</span>
          <strong>${metrics?.pub_ecpm ?? 0}</strong>
        </div>

        <div className="metric-box">
          <span>eCPM</span>
          <strong>${metrics?.ecpm ?? 0}</strong>
        </div>

      </div> */}

{/* DASHBOARD DATA TABS */}
<div className="dashboard-tabs-section">

  <div className="section-heading">
    <div>
      <h2>Performance Overview</h2>
      <p>Detailed dashboard performance data</p>
    </div>
  </div>


  {/* TABS */}
  <div className="performance-tabs">

    <button
      type="button"
      className={`performance-tab ${
        activeDataTab === "day_wise" ? "active" : ""
      }`}
      onClick={() => setActiveDataTab("day_wise")}
    >
      Day Wise

      {/* <span className="tab-count">
        {tabsData?.day_wise?.length || 0}
      </span> */}
    </button>


    <button
      type="button"
      className={`performance-tab ${
        activeDataTab === "top_ad_units" ? "active" : ""
      }`}
      onClick={() => setActiveDataTab("top_ad_units")}
    >
      Top Ad Units

      {/* <span className="tab-count">
        {tabsData?.top_ad_units?.length || 0}
      </span> */}
    </button>


    <button
      type="button"
      className={`performance-tab ${
        activeDataTab === "top_publishers" ? "active" : ""
      }`}
      onClick={() => setActiveDataTab("top_publishers")}
    >
      Top Publishers

      {/* <span className="tab-count">
        {tabsData?.top_publishers?.length || 0}
      </span> */}
    </button>

  </div>


  {/* TAB CONTENT */}
  <div className="dashboard-data-card">

    {tabsData && tabsData[activeDataTab]?.length > 0 ? (

      <>

        <div className="data-card-header">

          <div>
            <h3>
              {formatColumnName(activeDataTab)}
            </h3>

            <p className="table-description">
              Showing detailed {formatColumnName(activeDataTab).toLowerCase()} performance data
            </p>
          </div>

          <span className="data-card-number">
            {tabsData[activeDataTab].length}
          </span>

        </div>


        <div className="table-responsive dashboard-inner-table">

          <table className="table table-hover table-bordered">

            <thead>
              <tr>

                {Object.keys(tabsData[activeDataTab][0]).map(
                  (column, index) => (
                    <th key={index}>
                      {formatColumnName(column)}
                    </th>
                  )
                )}

              </tr>
            </thead>


            <tbody>

              {tabsData[activeDataTab].map((row, rowIndex) => (

                <tr key={rowIndex}>

                  {Object.entries(row).map(
                    ([column, value], cellIndex) => (

                      <td key={cellIndex}>
                        {formatTableValue(column, value)}
                      </td>

                    )
                  )}

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      </>

    ) : (

      <div className="no-dashboard-data">
        No data available for this section.
      </div>

    )}

  </div>

</div>
    </section>
  );
};

export default AdminDashboard;